import { Injectable, OnDestroy } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { Footnote } from 'src/app/models/footnote.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'
import { MainService } from 'src/app/services/main.service'

import { Overview } from './overview.model'

const profileInd = 0
const riskIndex = 1
const perfIndex = 2
const priceIndex = 3
const contentIndex = 4
const minIndex = 5
const expenseIndex = 6
const marketPriceIndex = 7

@Injectable({
  providedIn: 'root',
})
export class OverviewService implements OnDestroy {
  overviewModel: Overview
  overviewModel$: Observable<Overview>
  existingFootnotes: string[] = []
  aemContentSub: Subscription
  footNoteAem: AEMChunk
  expenseRatio: AEMChunk

  constructor(
    private readonly mainService: MainService,
    private readonly footnoteService: FootnoteService,
    private readonly centralContentService: CentralContentService,
  ) {}

  /*eslint complexity: [2, 10] */
  getModelData(): Observable<Overview> {
    return new Observable((subscriber) => {
      this.mainService
        .getOverviewObservable()
        .pipe(take(1))
        .subscribe((ovObs) => {
          if (
            Object.keys(ovObs).length !== 0 &&
            ovObs[0]?.name !== 'HTTPError' &&
            this.overviewModel === undefined
          ) {
            const profPos = ovObs[profileInd]?.fundProfile
            const contentPos = ovObs[contentIndex]
            const riskPos = ovObs[riskIndex]
            const perfPos = ovObs[perfIndex]
            const pricePos = ovObs[priceIndex]?.currentPrice?.dailyPrice
            const minPos = ovObs[minIndex]
            const priceCompound = ovObs[priceIndex]?.currentPrice?.yield
            const expensePos = ovObs[expenseIndex]
            const marketPricePos = ovObs[marketPriceIndex]
            this.overviewModel = {
              fundId: profPos?.fundId,
              ticker: profPos?.ticker,
              managementStyle: profPos?.fundManagementStyle,
              assetClass: profPos?.type,
              category: profPos?.category,
              cusip: profPos?.cusip,
              iovticker: profPos?.IOVTicker,
              inceptionDate: profPos?.inceptionDate,
              isInternalFund: profPos?.isInternalFund,
              isExternalFund: profPos?.isExternalFund,
              productSummary: contentPos?.productSummary,
              overview: contentPos?.overview,
              managerName: contentPos?.managerName,
              managerTenure: contentPos?.managerTenure,
              isETF: profPos?.isETF,
              isMutualFund: profPos?.isMutualFund,
              is529: profPos?.is529,
              isFundOfFunds: profPos?.fundFact?.isFundOfFunds,
              fundManagement:
                contentPos?.advisorNames !== undefined &&
                contentPos?.advisorNames?.length > 0 &&
                contentPos?.advisorNames[0].name
                  ? contentPos?.advisorNames[0]?.name
                  : [],
              riskCode: riskPos?.code,
              ytdMarket: perfPos?.YTD?.marketPrice,
              ytdAsOfDate: perfPos?.YTD?.asOfDate,
              navPrice: pricePos?.regular.price,
              navPriceAsOfDate: pricePos?.regular?.asOfDate,
              marketPrice: pricePos?.market?.price,
              marketPriceAsOfDate: pricePos?.market?.asOfDate,
              expenseRatio: profPos?.expenseRatio,
              expenseRatioAsOfDate: profPos?.expenseRatioAsOfDate,
              calendarYTDPct:
                perfPos?.recentInvestmentRtn?.fundReturn?.calendarYTDPct,
              calendarYTDPctNav:
                perfPos?.monthEndAvgAnnualRtn?.marketPriceFundReturn
                  ?.calendarYTDPct,
              regular: perfPos?.YTD?.regular,
              isMoneyMarket: profPos?.fundFact?.isMoneyMarket,
              secDesignation: profPos?.secDesignation,
              navPriceChangeAmount: pricePos?.regular?.priceChangeAmount,
              navPriceChangePct: pricePos?.regular?.priceChangePct,
              marketPriceChangeAmount: pricePos?.market?.priceChangeAmount,
              marketPriceChangePct: pricePos?.market?.priceChangePct,
              isClosed: profPos?.fundFact?.isClosed,
              isClosedToNewInvestors: profPos?.fundFact?.isClosedToNewInvestors,
              minimum: minPos?.general?.initial,
              isMarketNeutralFund: profPos?.fundFact?.isMarketNeutralFund,
              fundCategoryId: profPos?.fundCategory?.low?.id,
              dailyMarketPrice: pricePos?.dailyMarketValue?.price,
              dailyMarketAsOfDate: pricePos?.dailyMarketValue?.asOfDate,
              compoundYieldPct: priceCompound?.compoundYieldPct,
              compoundYieldAsOfDate: priceCompound?.asOfDate,
              prospectusExpenseRatio: expensePos?.prospectusExpenseRatio,
              prospectusExpenseRatioAsOfDate:
                expensePos?.prospectusExpenseRatioAsOfDate,
              contentMarketNeutralFootNote:
                ovObs[contentIndex]?.marketNeutralFootNote,
              closingMarketPrice: marketPricePos['@closingMarketPrice'],
            }
            this.setFootnotes()
          }
          subscriber.next(this.overviewModel)
          subscriber.complete()
        })
    })
  }
  parseAEMContent(content: any) {
    const feesExpenseNote = 'fund_definitions_acquired_fund_fees_and_expenses'
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (this.existingFootnotes.indexOf(feesExpenseNote) !== -1) {
      return
    }
    this.footNoteAem = this.centralContentService.getContentByBody(
      feesExpenseNote,
      content,
    )
    this.existingFootnotes.push(feesExpenseNote)
    this.overviewModel.expenseRatioFootnote = {
      footnote: new Footnote(
        this.footNoteAem.contentValue,
        'overview',
        this.footNoteAem.isAEM,
        'fund_definitions_acquired_fund_fees_and_expenses',
      ),
      isAEM: this.footNoteAem?.isAEM,
    }
    this.footnoteService.addFootnote(
      this.overviewModel.expenseRatioFootnote?.footnote,
    )
  }
  private setFootnotes() {
    if (this.overviewModel.isFundOfFunds && this.overviewModel.isMutualFund) {
      this.aemContentSub = this.centralContentService.aemContent$.subscribe(
        (content) => {
          this.parseAEMContent(content)
        },
      )
    } else {
      if (
        this.overviewModel.isMarketNeutralFund ||
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        this.overviewModel.fundCategoryId === 1416
      ) {
        this.aemContentSub = this.centralContentService.aemContent$.subscribe(
          (content) => {
            this.parseAEMContentExpenseRatio(content)
          },
        )
        this.overviewModel.expenseRatioFootnote = {
          footnote: new Footnote(
            this.expenseRatio.contentValue,
            'overview',
            this.expenseRatio.isAEM,
            'footnote_expense_ratio',
          ),
          isAEM: this.expenseRatio.isAEM,
        }
        this.footnoteService.addFootnote(
          this.overviewModel.expenseRatioFootnote?.footnote,
        )
      }
    }
  }
  parseAEMContentExpenseRatio(content: any) {
    if (
      this.overviewModel.ticker === 'VASFX' ||
      this.overviewModel.ticker === 'VMNIX' ||
      this.overviewModel.ticker === 'VMNFX'
    ) {
      this.expenseRatio = {
        contentValue: this.overviewModel.contentMarketNeutralFootNote,
        isAEM: true,
      }
    } else {
      this.expenseRatio = this.centralContentService.getContentByBody(
        'footnote_expense_ratio',
        content,
      )
    }
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
